import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomDialog } from '../context/DialogContext';
import { enableAudio, speakAlert } from '../utils/speech';
import usePWAInstall from '../hooks/usePWAInstall';

const WaiterDashboard = () => {
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const { alert: customAlert } = useCustomDialog();
    usePWAInstall();

    const [orders, setOrders] = useState([]);
    const [serviceRequests, setServiceRequests] = useState([]);
    const [kitchenAlerts, setKitchenAlerts] = useState([]);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'alerts'

    useEffect(() => {
        const storedToken = localStorage.getItem('staff_token');
        const storedUser = localStorage.getItem('staff_user');
        
        if (storedToken && storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser.role !== 'waiter' && parsedUser.role !== 'admin') {
                navigate('/staff-login');
                return;
            }
            setToken(storedToken);
            setUser(parsedUser);
        } else {
            navigate('/staff-login');
        }
    }, [navigate]);

    const prevOrdersRef = useRef([]);
    const prevRequestsRef = useRef([]);
    const prevAlertIds = useRef(new Set());
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    
    const unlockAudio = () => {
        enableAudio();
        setAudioUnlocked(true);
    };

    const fetchAlerts = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_alerts.php?action=fetch&target_role=waiter', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success && json.alerts) {
                // To avoid repeating speech for old alerts, we track what we've spoken
                json.alerts.forEach(alert => {
                    if (!prevAlertIds.current.has(alert.id)) {
                        speakAlert(alert.message_text);
                    }
                });
                // Update our spoken set
                prevAlertIds.current = new Set(json.alerts.map(a => a.id));
                // Set exactly what the server gives us
                setKitchenAlerts(json.alerts);
            }
        } catch(err) {
            console.error("Alerts fetch error:", err);
        }
    }, [token]);

    const fetchData = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_waiter_dashboard.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401) {
                localStorage.removeItem('staff_token');
                localStorage.removeItem('staff_user');
                navigate('/staff-login');
                return;
            }
            const json = await res.json();
            if (json.success) {
                const newReady = json.orders.filter(o => o.kitchen_status === 'Ready');
                const oldReady = prevOrdersRef.current.filter(o => o.kitchen_status === 'Ready');
                
                if (json.service_requests.length > prevRequestsRef.current.length || newReady.length > oldReady.length) {
                    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                    audio.play().catch(e => console.log("Sound play blocked by browser"));
                }
                
                prevOrdersRef.current = json.orders;
                prevRequestsRef.current = json.service_requests;

                setOrders(json.orders);
                setServiceRequests(json.service_requests);
            }
        } catch (err) {
            console.error("Waiter Dashboard fetch error:", err);
        }
    }, [token, navigate]);

    useEffect(() => {
        fetchData();
        fetchAlerts();
        
        const interval = setInterval(() => {
            fetchData();
            fetchAlerts();
        }, 15000); // Poll every 15s

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchData();
                fetchAlerts();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchData, fetchAlerts]);

    const handleResolveRequest = async (id) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_waiter_dashboard.php?action=resolve_request', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ request_id: id })
            });
            const json = await res.json();
            if (json.success) {
                fetchData();
            } else {
                customAlert('Failed to resolve request', 'Error', 'danger');
            }
        } catch (err) {
            customAlert('Network error', 'Error', 'danger');
        }
    };

    const handleDismissKitchenAlert = async (id) => {
        try {
            await fetch('https://menu.searchisend.in/apimenu/v2/v2_alerts.php?action=resolve', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ alert_id: id })
            });
            setKitchenAlerts(prev => prev.filter(a => a.id !== id));
        } catch(err) {
            console.error(err);
        }
    };

    const handleTakeOrder = () => {
        if (user && user.restaurant_username) {
            navigate(`/${user.restaurant_username}?waiterMode=true`);
        }
    };
    
    const handleLogout = () => {
        localStorage.removeItem('staff_token');
        localStorage.removeItem('staff_user');
        navigate('/staff-login');
    };

    if (!token) return null;

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>
            <header style={{ background: '#1e293b', color: '#fff', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '4px solid var(--primary-orange)', position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ width: '45px', height: '45px', background: 'var(--primary-orange)', color: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        <i className="fas fa-concierge-bell" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800' }}>Waiter Command Center</h2>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>{user?.name} | Real-time Table Management</p>
                    </div>
                </div>
                <button 
                    onClick={handleLogout}
                    style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '10px 20px', borderRadius: '50px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', transition: 'all 0.2s' }}
                >
                    <i className="fas fa-sign-out-alt" style={{ marginRight: '5px' }} /> End Shift
                </button>
            </header>

            {!audioUnlocked && (
                <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', color: '#1e293b', padding: '12px', textAlign: 'center', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }} onClick={unlockAudio}>
                    <i className="fas fa-volume-up" style={{ color: 'var(--primary-orange)', fontSize: '1.2rem' }}></i>
                    <strong>Tap here once to enable Kitchen Audio Alerts</strong>
                </div>
            )}

            <main style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', paddingBottom: '100px' }}>
                
                {/* Tabs */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: '#e2e8f0', padding: '5px', borderRadius: '12px' }}>
                    <button 
                        onClick={() => setActiveTab('orders')}
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: activeTab === 'orders' ? '#fff' : 'transparent', color: activeTab === 'orders' ? 'var(--primary-orange)' : '#64748b', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', boxShadow: activeTab === 'orders' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}
                    >
                        <i className="fas fa-fire-burner" style={{ marginRight: '8px' }}></i> Active Orders
                    </button>
                    <button 
                        onClick={() => setActiveTab('alerts')}
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: activeTab === 'alerts' ? '#fff' : 'transparent', color: activeTab === 'alerts' ? '#eab308' : '#64748b', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', boxShadow: activeTab === 'alerts' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', position: 'relative' }}
                    >
                        <i className="fas fa-bell" style={{ marginRight: '8px' }}></i> Alerts & Requests
                        {(serviceRequests.length > 0 || kitchenAlerts.length > 0) && (
                            <span style={{ position: 'absolute', top: '8px', right: '10px', background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: '50px', fontSize: '0.75rem' }}>
                                {serviceRequests.length + kitchenAlerts.length}
                            </span>
                        )}
                    </button>
                </div>

                {activeTab === 'alerts' && (
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fas fa-bell" style={{ color: '#eab308' }}></i> All Requests & Alerts
                        </h3>
                        
                        {(serviceRequests.length === 0 && kitchenAlerts.length === 0) ? (
                            <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1' }}>
                                No active requests or alerts
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {/* Kitchen Alerts */}
                                {kitchenAlerts.map((alert, index) => (
                                    <div key={`ka-${index}`} style={{ background: '#fff', padding: '15px 20px', borderRadius: '16px', borderLeft: '4px solid #ef4444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <div>
                                            <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#1e293b' }}><i className="fas fa-exclamation-circle" style={{ color: '#ef4444', marginRight: '6px' }}></i> Kitchen Alert</div>
                                            <div style={{ color: '#475569', fontSize: '0.95rem', marginTop: '4px' }}>{alert.message_text}</div>
                                            <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '6px' }}>
                                                <i className="far fa-clock" style={{ marginRight: '4px' }}></i> {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleDismissKitchenAlert(alert.id)}
                                            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '50px', fontWeight: '700', color: '#64748b', cursor: 'pointer' }}
                                        >
                                            <i className="fas fa-times" style={{ marginRight: '6px' }}></i> Dismiss
                                        </button>
                                    </div>
                                ))}

                                {/* Table Pings */}
                                {serviceRequests.map(req => (
                                    <div key={`sr-${req.id}`} style={{ background: '#fff', padding: '15px 20px', borderRadius: '16px', borderLeft: '4px solid #eab308', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <div>
                                            <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#1e293b' }}>Table {req.table_number}</div>
                                            <div style={{ color: '#475569', fontSize: '0.95rem', marginTop: '4px' }}>Requested: <strong style={{ color: '#eab308' }}>{req.request_type}</strong></div>
                                            <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '6px' }}>
                                                <i className="far fa-clock" style={{ marginRight: '4px' }}></i> {new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleResolveRequest(req.id)}
                                            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '50px', fontWeight: '700', color: '#10b981', cursor: 'pointer' }}
                                        >
                                            <i className="fas fa-check" style={{ marginRight: '6px' }}></i> Acknowledge
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'orders' && (
                    <div>
                        <h3 style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="fas fa-fire-burner" style={{ color: 'var(--primary-orange)' }}></i> Kitchen Status
                        </h3>
                        
                        {orders.length === 0 ? (
                            <div style={{ background: '#fff', padding: '20px', borderRadius: '16px', textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1' }}>
                                No active kitchen orders
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
                                {orders.map(order => (
                                    <div key={order.id} style={{ background: '#fff', padding: '20px', borderRadius: '16px', border: `2px solid ${order.kitchen_status === 'Ready' ? '#10b981' : 'transparent'}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <div>
                                                <div style={{ fontWeight: '800', fontSize: '1.2rem', color: '#1e293b' }}>Table {order.table_no || 'Takeaway'}</div>
                                                <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '2px' }}>{order.customer_name || 'Guest'}</div>
                                            </div>
                                            <span style={{ 
                                                padding: '4px 12px', 
                                                borderRadius: '50px', 
                                                fontSize: '0.75rem', 
                                                fontWeight: '800',
                                                background: order.kitchen_status === 'Ready' ? '#dcfce7' : '#fef3c7',
                                                color: order.kitchen_status === 'Ready' ? '#166534' : '#b45309'
                                            }}>
                                                {order.kitchen_status}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '10px', marginTop: '10px' }}>
                                            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            <div style={{ fontWeight: '700', color: '#1e293b' }}>₹{parseFloat(order.grand_total).toFixed(2)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

            </main>

            {/* Sticky Action Button */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                background: 'linear-gradient(to top, rgba(255,255,255,1) 50%, rgba(255,255,255,0))',
                padding: '20px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 100
            }}>
                <button 
                    onClick={handleTakeOrder}
                    style={{
                        background: 'var(--primary-orange)',
                        color: '#fff',
                        border: 'none',
                        padding: '18px 40px',
                        borderRadius: '50px',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        boxShadow: '0 10px 25px rgba(234,88,12,0.4)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        maxWidth: '500px',
                        width: '100%'
                    }}
                >
                    <i className="fas fa-plus-circle" style={{ fontSize: '1.4rem' }}></i>
                    Take New Order
                </button>
            </div>
        </div>
    );
};

export default WaiterDashboard;
